import React, {
	useMemo,
	useState,
} from 'react';
import {
	Card,
	Col,
	Empty,
	Row,
} from 'antd';
import { useQuery } from 'react-query';
import {
	formatDate,
	toMoment,
} from '@shared/utils/date';
import {
	DATE_AND_TIME,
	TsReportTypes,
} from '@shared/utils/constants';
import isItemPortCall from '@shared/utils/isItemPortCall';
import { formatAndRoundNumber } from '@shared/utils/formatNumber';
import Table from '@client/components/Table/Table';
import {
	ItineraryPortCallDto,
	ItinerarySeaPassageDto,
} from '@client/screens/estimates/details/helpers/types';
import {
	getTheShipSeaPassageDetails,
	getThirdPartyNoonReports,
} from '@client/lib/api';
import TsReport from './TsReport';

const TheShipReportsTab = ({
	selectedEntry,
}: {
	selectedEntry: ItineraryPortCallDto | ItinerarySeaPassageDto;
}) => {
	const isPortCall = isItemPortCall(selectedEntry);
	const isActual = selectedEntry.arrivalDate != null && selectedEntry.departureDate != null;
	const [selectedReportId, setSelectedReportId] = useState<string | undefined>();

	const from = isPortCall ? selectedEntry.arrivalDate : selectedEntry.departureDate;
	const to = isPortCall ? selectedEntry.departureDate : selectedEntry.arrivalDate;

	const {
		data: tsLogReports,
		isLoading: reportsLoading,
	} = useQuery(
		['tsLogReports', selectedEntry?.id],
		() => getThirdPartyNoonReports(
			selectedEntry.vesselId,
			{
				from: toMoment(from!).toISOString(),
				to: toMoment(to!).toISOString(),
				portCallId: isPortCall ? (selectedEntry.externalId ?? undefined) : undefined,
			},
		),
		{
			enabled: from != null && to != null && isActual,
			refetchOnWindowFocus: false,
			retry: false,
		},
	);

	const {
		data: tsSeaPassageDetails,
	} = useQuery(
		['tsSeaPassageDetails', selectedEntry?.id],
		() => getTheShipSeaPassageDetails(
			selectedEntry.vesselId,
			selectedEntry.id,
		),
		{
			enabled: !isPortCall,
			refetchOnWindowFocus: false,
			retry: false,
		},
	);

	const selectedReport = useMemo(() => {
		if (selectedReportId == null) {
			return undefined;
		}

		const report = tsLogReports?.find((r) => r.id === selectedReportId);

		if (report == null) {
			return undefined;
		}

		return report;
	}, [selectedReportId, tsLogReports]);

	const totalConsumption = useMemo(() => {
		if (tsSeaPassageDetails == null || tsSeaPassageDetails.consumptions == null) {
			return 0;
		}

		const total = tsSeaPassageDetails.consumptions.reduce((acc, curr) => (
			acc + curr.consumedSospEosp
		), 0);

		return total;
	}, [tsSeaPassageDetails]);

	return (
		<Row gutter={[16, 16]}>
			{tsSeaPassageDetails?.vessel?.name != null && (
				<>
					<Col span={4}>
						<Card title="Vessel" size="small">
							{`${tsSeaPassageDetails.vessel.name}`}
						</Card>
					</Col>
					<Col span={4}>
						<Card title="Total consumption" size="small">
							{`${formatAndRoundNumber(totalConsumption, 3)} MT`}
						</Card>
					</Col>
					<Col span={4}>
						<Card title="Total emissions" size="small">
							{`${formatAndRoundNumber(tsSeaPassageDetails.co2EmissionSospEosp, 0)} MT CO2`}
						</Card>
					</Col>
					<Col span={4}>
						<Card title="Total EUA's" size="small">
							{`${formatAndRoundNumber(tsSeaPassageDetails.euaDepartureArrival, 0)} EUA's`}
						</Card>
					</Col>
					<Col span={4}>
						<Card title="Total distance" size="small">
							{`${formatAndRoundNumber(tsSeaPassageDetails.distanceSospEosp, 0)} nm`}
						</Card>
					</Col>
					<Col span={4}>
						<Card title="Avg. speed" size="small">
							{`${formatAndRoundNumber(tsSeaPassageDetails.avgSpeedSogSospEosp, 2)} knots`}
						</Card>
					</Col>
				</>
			)}
			<Col span={8}>
				<Table
					loading={reportsLoading}
					pagination={false}
					size="small"
					dataSource={tsLogReports}
					onRowClick={(row) => setSelectedReportId(row.id)}
					locale={{ emptyText: 'No reports found' }}
					columns={[
						{
							title: 'Report',
							dataIndex: 'type',
							render: (type) => (type == null ? 'Unknown' : TsReportTypes[type as keyof typeof TsReportTypes]),
						},
						{
							title: 'Title',
							dataIndex: 'dateAndTime',
							render: (d) => (d == null ? 'Unknown' : formatDate(d, DATE_AND_TIME)),
						},
					]}
				/>
			</Col>
			<Col span={16}>
				<Card size="small">
					{selectedReport == null ? (
						<Empty description="Select a report to see it" />
					) : (
						<TsReport report={selectedReport} />
					)}
				</Card>
			</Col>
		</Row>
	);
};

export default TheShipReportsTab;
